import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { registerLocale } from "react-datepicker";
import toast, { Toaster } from "react-hot-toast";
import DatePicker from "react-datepicker";

import ru from "date-fns/locale/ru";
import moment from "moment";
import clsx from "clsx";

import {
  actionGetIntegrationErrors,
  actionSetIntegrationErrors,
} from "../../../redux/action/easymerch/actionIntegrationErrors";
import {
  reducerIntegrationErrorsInputValues,
  reducerIntegrationErrorsStatus,
} from "../../../redux/reducer/easymerch/reducers/reducerIntegrationErrors";
import { generateStyles } from "../../../utils/generateToastStyles";
import { getLabelByValue } from "../../../utils/getLabelByValue";
import { AppDispatch } from "../../../redux/reducer/store";

import CustomMultiSelect from "../../custom/customMultiSelect/customMultiSelect";
import LimitSelect from "../../custom/limitSelect/limitSelect";
import ToastPopup from "../../custom/toastPopup/toastPopup";
import Pagination from "../../pagination/pagination";
import Select from "../../custom/select/select";
import Loader from "../../custom/loader/loader";

import "react-datepicker/dist/react-datepicker.css";
import "../table.scss";
import { IUserOptions } from "../../../types/interfaces";

const IS_VISIBLE_SIDEBAR = process.env.REACT_APP_IS_VISIBLE_SIDEBAR;

interface IintegrationError {
  address: string;
  contract: { id: number; name: string };
  date: string;
  description: string;
  emEmployee: { id: number; name: string };
  isSingleTrip: boolean;
  merch: { id: number; name: string };
  personId: number;
  pm: { id: number; name: string };
  pos: { id: number; name: string };
  project: { id: number; name: string };
  shopId: number;
  status: { [key: string]: string };
  sv: { id: number; name: string };
  type: string;
  userId: number;
  visitDate: string;
  visitNo: number;
  vsv: { id: number; name: string };
}

const emVisitOptions = [
  { value: "", label: "Не указано" },
  { value: true, label: "Да" },
  { value: false, label: "Нет" },
];

const typesOptions = [
  { value: "", label: "Все" },
  { value: "Other", label: "Другое" },
  { value: "Report", label: "Отчет" },
  { value: "Pos", label: "Торговая точка" },
  { value: "Employee", label: "Физ. лицо" },
];

const typeOptionValues = [
  { value: "BadUser", label: "Физ. лицо" },
  { value: "BadPos", label: "Торговая точка" },
  { value: "WithoutCheckout", label: "Отчет" },
  { value: "NotMerchVisit", label: "Отчет" },
  { value: "UnknownServiceType", label: "Отчет" },
  { value: "NotMappedPos", label: "Отчет" },
  { value: "NotMappedUser", label: "Отчет" },
  { value: "MismatchEmployee", label: "Отчет" },
  { value: "MismatchContracts", label: "Отчет" },
  { value: "MismatchPos", label: "Отчет" },
  { value: "VisitNotFound", label: "Отчет" },
];

const TableIntegrationErrors: FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const {
    integrationErrorsInputValues,
    integrationErrorsCount,
    integrationErrors,
    contractOptions,
    projectOptions,
    merchsOptions,
    vsvOptions,
    svOptions,
    offset,
    status,
    limit,
  } = useSelector((state: any) => state.easymerch.integrationErrors);
  const { isOpenSidebar } = useSelector(
    (state: IUserOptions) => state.user.user
  );
  const [isInputChange, setIsInputChange] = useState<boolean>(false);
  const [isPending, setIsPending] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [startPage, setStartPage] = useState<number>(1);
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    integrationErrorsInputValues.date &&
      new Date(integrationErrorsInputValues.date)
  );
  const actualLimit =
    JSON.parse(localStorage.getItem("pageLimit") as string) || limit;

  useEffect(() => {
    registerLocale("ru", ru);
    setIsPending(true);
    dispatch(actionGetIntegrationErrors(actualLimit, offset)).then(() => {
      setIsPending(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status === 200) {
      toast.dismiss();
      toast(
        (t) => (
          <ToastPopup
            title="Ошибки интеграции успешно выгружены!"
            isSuccess={true}
            toast={toast}
            t={t}
          />
        ),
        generateStyles(true)
      );
    } else if (status === 204) {
      toast.dismiss();
      toast(
        (t) => (
          <ToastPopup
            title="Данные успешно загружены."
            isSuccess={true}
            toast={toast}
            t={t}
          />
        ),
        generateStyles(true)
      );
    } else if (status === 1) {
      toast.dismiss();
      toast(
        (t) => (
          <ToastPopup
            title="При загрузке произошла ошибка!"
            isSuccess={false}
            toast={toast}
            t={t}
          />
        ),
        generateStyles()
      );
    } else if (status === 0) {
      toast.dismiss();
      toast(
        (t) => (
          <ToastPopup
            title="При выгрузке произошла ошибка!"
            isSuccess={false}
            toast={toast}
            t={t}
          />
        ),
        generateStyles()
      );
    }

    setTimeout(() => {
      dispatch(reducerIntegrationErrorsStatus(null));
    }, 5000);
  }, [dispatch, status]);

  useEffect(() => {
    const handleDocumentClick = () => {
      setIsPending(true);
      dispatch(actionGetIntegrationErrors(limit, 0)).then(() => {
        setIsPending(false);
      });
      setIsInputChange(false);
    };

    if (isInputChange) {
      document.addEventListener("click", handleDocumentClick);
      return () => {
        document.removeEventListener("click", handleDocumentClick);
      };
    } else
      return () => {
        document.removeEventListener("click", handleDocumentClick);
      };
  }, [dispatch, isInputChange, limit]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;

    dispatch(
      reducerIntegrationErrorsInputValues({
        ...integrationErrorsInputValues,
        [name]: value,
      })
    );

    setIsInputChange(true);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      setIsPending(true);
      dispatch(actionGetIntegrationErrors(limit, offset)).then(() => {
        setIsPending(false);
      });
      setIsInputChange(false);
    }
  };

  const handleTypesSelectChange = (
    event: { value?: string; label?: string },
    name: string
  ) => {
    const selectedValue = event?.value;
    dispatch(
      reducerIntegrationErrorsInputValues({
        ...integrationErrorsInputValues,
        [name]: !selectedValue ? [] : [selectedValue],
      })
    );
    setIsPending(true);
    dispatch(actionGetIntegrationErrors(limit, offset)).then(() => {
      setIsPending(false);
    });
    setIsInputChange(false);
  };

  const handleIsSingleTripSelectChange = (
    event: { value: string; label: string },
    name: string
  ) => {
    const selectedValue = event?.value;
    const selectedName = event?.label;
    dispatch(
      reducerIntegrationErrorsInputValues({
        ...integrationErrorsInputValues,
        [name]: selectedValue,
      })
    );
    setIsPending(true);
    dispatch(
      actionSetIntegrationErrors(
        {
          ...integrationErrorsInputValues,
          [name]: selectedValue,
        },
        limit,
        offset,
        selectedName
      )
    ).then(() => {
      setIsPending(false);
    });
    setIsInputChange(false);
  };

  const handleDatePickerChange = (date: Date) => {
    setSelectedDate(date);
    const formattedDate = moment(date).format("YYYY-MM-DD");
    dispatch(
      reducerIntegrationErrorsInputValues({
        ...integrationErrorsInputValues,
        date: formattedDate,
      })
    );
    setIsPending(true);
    dispatch(actionGetIntegrationErrors(limit, offset)).then(() => {
      setIsPending(false);
    });
    setIsInputChange(false);
  };

  const handleDateKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
  };

  const handleClearDate = () => {
    if (selectedDate) {
      setSelectedDate(null);
      dispatch(
        reducerIntegrationErrorsInputValues({
          ...integrationErrorsInputValues,
          date: "",
        })
      );
      setIsPending(true);
      dispatch(actionGetIntegrationErrors(limit, offset)).then(() => {
        setIsPending(false);
      });
      setIsInputChange(false);
    }
  };

  const handleChange = (newLimit: number, newOffset: number) => {
    setIsPending(true);
    dispatch(actionGetIntegrationErrors(newLimit, newOffset)).then(() => {
      setIsPending(false);
    });
    localStorage.setItem("pageLimit", JSON.stringify(newLimit));
  };

  const handleChangeLimits = (option: { value: number }) => {
    handleChange(option.value, 0);
    setCurrentPage(1);
    setStartPage(1);
  };

  const renderTableRow = (
    integrationError: IintegrationError,
    index: number
  ) => {
    const {
      isSingleTrip,
      description,
      visitDate,
      personId,
      contract,
      visitNo,
      address,
      project,
      userId,
      merch,
      type,
      vsv,
      sv,
    } = integrationError;
    const formattedDate = visitDate?.split("-")?.reverse()?.join(".");
    return (
      <tr key={index}>
        <td>{formattedDate}</td>
        <td>{getLabelByValue(type, typeOptionValues)}</td>
        <td>{description}</td>
        <td>{visitNo}</td>
        <td>{address}</td>
        <td>{contract?.name}</td>
        <td>{project?.name}</td>
        <td>{merch?.name}</td>
        <td>{sv?.name}</td>
        <td>{vsv?.name}</td>
        <td>{personId}</td>
        <td>{userId}</td>
        <td>{!isSingleTrip ? "Нет" : "Да"}</td>
      </tr>
    );
  };
  return (
    <>
      <div className="loader__wrapper">
        {(!integrationErrors || isPending) && <Loader />}
      </div>
      <div
        className={clsx(
          JSON.parse(IS_VISIBLE_SIDEBAR as string) &&
            isOpenSidebar &&
            "active__table",
          !isOpenSidebar && "closed__table",
          "table"
        )}
      >
        {status && status !== null && (
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 5000,
            }}
          />
        )}
        <div className="filters__container__mobile">
          <div className="filters__item__mobile">
            <div className="filter__data_picker filter__data_picker_excel">
              <DatePicker
                onChange={handleDatePickerChange}
                onKeyDown={handleDateKeyDown}
                placeholderText="Дата Визита"
                selected={selectedDate}
                dateFormat="dd.MM.yyyy"
                autoComplete="off"
                locale="ru"
                name="date"
              />
              {selectedDate && (
                <div className="clear__icon" onClick={handleClearDate} />
              )}
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={
                  integrationErrorsInputValues.types &&
                  integrationErrorsInputValues.types.length &&
                  typesOptions.find(
                    (option: { value: string; label: string }) =>
                      option.value === integrationErrorsInputValues.types[0]
                  )
                }
                propsChange={(event: { value: string; label: string }) =>
                  handleTypesSelectChange(event, "types")
                }
                isShowPlaceholder={integrationErrorsInputValues.types !== null}
                className="users__filter"
                options={typesOptions}
                placeholder="Тип"
                name="types"
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <input
                defaultValue={
                  integrationErrorsInputValues.description &&
                  integrationErrorsInputValues.description
                }
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder=" "
                name="description"
                type="text"
              />
              <label>Описание</label>
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <input
                defaultValue={
                  integrationErrorsInputValues.visitNo &&
                  integrationErrorsInputValues.visitNo
                }
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder=" "
                name="visitNo"
                type="text"
              />
              <label>ID Визита</label>
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <input
                defaultValue={
                  integrationErrorsInputValues.address &&
                  integrationErrorsInputValues.address
                }
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                autoComplete="off"
                placeholder=" "
                name="address"
                type="text"
              />
              <label>Адрес</label>
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="multiselect__filter_block">
              <CustomMultiSelect
                setLoading={setIsPending}
                reducer={reducerIntegrationErrorsInputValues}
                inputValues={integrationErrorsInputValues}
                action={actionGetIntegrationErrors}
                pageName="integrationErrors"
                options={contractOptions}
                isShowPlaceholder={true}
                placeholder="Контракт*"
                name="contracts"
                resetPages={() => {}}
                offset={offset}
                limit={limit}
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="multiselect__filter_block">
              <CustomMultiSelect
                setLoading={setIsPending}
                reducer={reducerIntegrationErrorsInputValues}
                inputValues={integrationErrorsInputValues}
                action={actionGetIntegrationErrors}
                pageName="integrationErrors"
                isShowPlaceholder={true}
                options={projectOptions}
                resetPages={() => {}}
                placeholder="Проект*"
                offset={offset}
                name="projects"
                limit={limit}
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="multiselect__filter_block">
              <CustomMultiSelect
                setLoading={setIsPending}
                reducer={reducerIntegrationErrorsInputValues}
                inputValues={integrationErrorsInputValues}
                action={actionGetIntegrationErrors}
                pageName="integrationErrors"
                placeholder="Мерчендайзер*"
                isShowPlaceholder={true}
                resetPages={() => {}}
                options={merchsOptions}
                offset={offset}
                limit={limit}
                name="merchs"
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="multiselect__filter_block">
              <CustomMultiSelect
                setLoading={setIsPending}
                reducer={reducerIntegrationErrorsInputValues}
                inputValues={integrationErrorsInputValues}
                action={actionGetIntegrationErrors}
                pageName="integrationErrors"
                placeholder="Супервайзер*"
                resetPages={() => {}}
                isShowPlaceholder={true}
                options={svOptions}
                offset={offset}
                limit={limit}
                name="sv"
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="multiselect__filter_block">
              <CustomMultiSelect
                setLoading={setIsPending}
                reducer={reducerIntegrationErrorsInputValues}
                inputValues={integrationErrorsInputValues}
                action={actionGetIntegrationErrors}
                pageName="integrationErrors"
                resetPages={() => {}}
                isShowPlaceholder={true}
                options={vsvOptions}
                placeholder="ВСВ*"
                offset={offset}
                limit={limit}
                name="vsv"
              />
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <input
                defaultValue={
                  integrationErrorsInputValues.personId &&
                  integrationErrorsInputValues.personId
                }
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder=" "
                name="personId"
                type="text"
              />
              <label>ID EM</label>
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <input
                defaultValue={
                  integrationErrorsInputValues.userId &&
                  integrationErrorsInputValues.userId
                }
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder=" "
                name="userId"
                type="text"
              />
              <label>ID физ. лица</label>
            </div>
          </div>
          <div className="filters__item__mobile">
            <div className="filter__block">
              <Select
                defaultValue={emVisitOptions.find(
                  (option: { value: boolean | string; label: string }) =>
                    option.value === integrationErrorsInputValues?.singleTrip
                )}
                propsChange={(event: { value: string; label: string }) =>
                  handleIsSingleTripSelectChange(event, "singleTrip")
                }
                isShowPlaceholder={
                  integrationErrorsInputValues?.singleTrip !== undefined
                }
                placeholder="Внеплановый визит"
                className="users__filter"
                options={emVisitOptions}
                name="singleTrip"
              />
            </div>
          </div>
        </div>
        <div className="table__wrapper">
          <table>
            <thead className="table__header__filters">
              <tr>
                <th>
                  <div className="filter__data_picker filter__data_picker_excel">
                    <DatePicker
                      onChange={handleDatePickerChange}
                      onKeyDown={handleDateKeyDown}
                      placeholderText="Дата Визита"
                      selected={selectedDate}
                      dateFormat="dd.MM.yyyy"
                      autoComplete="off"
                      locale="ru"
                      name="date"
                    />
                    {selectedDate && (
                      <div className="clear__icon" onClick={handleClearDate} />
                    )}
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={
                        integrationErrorsInputValues.types &&
                        integrationErrorsInputValues.types.length &&
                        typesOptions.find(
                          (option: { value: string; label: string }) =>
                            option.value ===
                            integrationErrorsInputValues.types[0]
                        )
                      }
                      propsChange={(event: { value: string; label: string }) =>
                        handleTypesSelectChange(event, "types")
                      }
                      isShowPlaceholder={
                        integrationErrorsInputValues.types !== null
                      }
                      className="users__filter"
                      options={typesOptions}
                      placeholder="Тип"
                      name="types"
                    />
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <input
                      defaultValue={
                        integrationErrorsInputValues.description &&
                        integrationErrorsInputValues.description
                      }
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder=" "
                      name="description"
                      type="text"
                    />
                    <label>Описание</label>
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <input
                      defaultValue={
                        integrationErrorsInputValues.visitNo &&
                        integrationErrorsInputValues.visitNo
                      }
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder=" "
                      name="visitNo"
                      type="text"
                    />
                    <label>ID Визита</label>
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <input
                      defaultValue={
                        integrationErrorsInputValues.address &&
                        integrationErrorsInputValues.address
                      }
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      autoComplete="off"
                      placeholder=" "
                      name="address"
                      type="text"
                    />
                    <label>Адрес</label>
                  </div>
                </th>
                <th>
                  <div className="multiselect__filter_block">
                    <CustomMultiSelect
                      setLoading={setIsPending}
                      reducer={reducerIntegrationErrorsInputValues}
                      inputValues={integrationErrorsInputValues}
                      action={actionGetIntegrationErrors}
                      pageName="integrationErrors"
                      options={contractOptions}
                      isShowPlaceholder={true}
                      placeholder="Контракт*"
                      name="contracts"
                      resetPages={() => {}}
                      offset={offset}
                      limit={limit}
                    />
                  </div>
                </th>
                <th>
                  <div className="multiselect__filter_block">
                    <CustomMultiSelect
                      setLoading={setIsPending}
                      reducer={reducerIntegrationErrorsInputValues}
                      inputValues={integrationErrorsInputValues}
                      action={actionGetIntegrationErrors}
                      pageName="integrationErrors"
                      isShowPlaceholder={true}
                      options={projectOptions}
                      resetPages={() => {}}
                      placeholder="Проект*"
                      offset={offset}
                      name="projects"
                      limit={limit}
                    />
                  </div>
                </th>
                <th>
                  <div className="multiselect__filter_block">
                    <CustomMultiSelect
                      setLoading={setIsPending}
                      reducer={reducerIntegrationErrorsInputValues}
                      inputValues={integrationErrorsInputValues}
                      action={actionGetIntegrationErrors}
                      pageName="integrationErrors"
                      placeholder="Мерчендайзер*"
                      isShowPlaceholder={true}
                      resetPages={() => {}}
                      options={merchsOptions}
                      offset={offset}
                      limit={limit}
                      name="merchs"
                    />
                  </div>
                </th>
                <th>
                  <div className="multiselect__filter_block">
                    <CustomMultiSelect
                      setLoading={setIsPending}
                      reducer={reducerIntegrationErrorsInputValues}
                      inputValues={integrationErrorsInputValues}
                      action={actionGetIntegrationErrors}
                      pageName="integrationErrors"
                      placeholder="Супервайзер*"
                      resetPages={() => {}}
                      isShowPlaceholder={true}
                      options={svOptions}
                      offset={offset}
                      limit={limit}
                      name="sv"
                    />
                  </div>
                </th>
                <th>
                  <div className="multiselect__filter_block">
                    <CustomMultiSelect
                      setLoading={setIsPending}
                      reducer={reducerIntegrationErrorsInputValues}
                      inputValues={integrationErrorsInputValues}
                      action={actionGetIntegrationErrors}
                      pageName="integrationErrors"
                      resetPages={() => {}}
                      isShowPlaceholder={true}
                      options={vsvOptions}
                      placeholder="ВСВ*"
                      offset={offset}
                      limit={limit}
                      name="vsv"
                    />
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <input
                      defaultValue={
                        integrationErrorsInputValues.personId &&
                        integrationErrorsInputValues.personId
                      }
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder=" "
                      name="personId"
                      type="text"
                    />
                    <label>ID EM</label>
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <input
                      defaultValue={
                        integrationErrorsInputValues.userId &&
                        integrationErrorsInputValues.userId
                      }
                      onChange={handleInputChange}
                      onKeyPress={handleKeyPress}
                      placeholder=" "
                      name="userId"
                      type="text"
                    />
                    <label>ID физ. лица</label>
                  </div>
                </th>
                <th>
                  <div className="filter__block">
                    <Select
                      defaultValue={emVisitOptions.find(
                        (option: { value: boolean | string; label: string }) =>
                          option.value ===
                          integrationErrorsInputValues?.singleTrip
                      )}
                      propsChange={(event: { value: string; label: string }) =>
                        handleIsSingleTripSelectChange(event, "singleTrip")
                      }
                      isShowPlaceholder={
                        integrationErrorsInputValues?.singleTrip !== undefined
                      }
                      placeholder="Внеплановый визит"
                      className="users__filter"
                      options={emVisitOptions}
                      name="singleTrip"
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {integrationErrors && integrationErrors.map(renderTableRow)}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <div className="pagination__count">
            Всего: {integrationErrorsCount}
          </div>
          <div className="pagination__str">
            <span>Отображать строки</span>
            <LimitSelect limit={actualLimit} propsChange={handleChangeLimits} />
          </div>
          <Pagination
            setCurrentPage={setCurrentPage}
            count={integrationErrorsCount}
            setStartPage={setStartPage}
            handleChange={handleChange}
            currentPage={currentPage}
            startPage={startPage}
            limit={actualLimit}
          />
        </div>
      </div>
    </>
  );
};

export default TableIntegrationErrors;
